// Keep the stored lotus field for compatibility with existing daily records.
type DailyRecord = { date: string; streak: number; lotus: number; dates: string[] };
const key = 'heart-sutra-daily-v1';
const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
const datePattern = /^\d{4}-\d{2}-\d{2}$/;
const emptyRecord = (): DailyRecord => ({date:'',streak:0,lotus:0,dates:[]});
export function parseRecord(data: unknown): DailyRecord | undefined {
 if(data && typeof data === 'object' && 'date' in data && 'streak' in data && 'lotus' in data && typeof data.date === 'string' && datePattern.test(data.date) && Number.isSafeInteger(data.streak) && Number.isSafeInteger(data.lotus) && Number(data.streak)>0 && Number(data.lotus)>0) {
  const old = data as DailyRecord;
  return {...old, dates: Array.from(new Set([...(Array.isArray(old.dates) ? old.dates.filter((d: unknown): d is string => typeof d === 'string' && datePattern.test(d)) : []), old.date]))};
 }
}
function read(): DailyRecord {
 try {
  return parseRecord(JSON.parse(localStorage.getItem(key) || 'null')) ?? emptyRecord();
 } catch { /* Storage may be unavailable in private browsing. */ }
 return emptyRecord();
}
// Older records may hold more stamps than listed dates, so each side keeps its count plus the other's new days.
export function mergeRecords(a: DailyRecord, b: DailyRecord): DailyRecord {
 const dates = Array.from(new Set([...a.dates, ...b.dates])).sort();
 const date = dates.at(-1) ?? '';
 const lotus = Math.max(a.lotus + b.dates.filter(d => !a.dates.includes(d)).length, b.lotus + a.dates.filter(d => !b.dates.includes(d)).length);
 let streak = 0;
 for(const cursor = new Date(`${date}T00:00`); dates.includes(localDate(cursor)); cursor.setDate(cursor.getDate()-1))streak++;
 const stored = Math.max(...[a, b].filter(r => r.date === date).map(r => r.streak), 0);
 return {date, streak: Math.max(streak, stored), lotus, dates};
}
export function mountDaily(onClaim: () => void, onPageTurn: () => void) {
 const button = document.getElementById('daily-claim') as HTMLButtonElement;
 const replay = document.getElementById('daily-replay') as HTMLButtonElement;
 const status = document.getElementById('daily-status')!;
 const dialog = document.getElementById('daily-dialog') as HTMLDialogElement;
 const bookScene = document.getElementById('book-scene')!;
 const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
 const grid = document.getElementById('calendar-grid')!;
 const calendarStatus = document.getElementById('calendar-status')!;
 let month = new Date();month.setDate(1);
 let record = read();
 let animationTimer: ReturnType<typeof setTimeout> | undefined;
 function drawCalendar(stampDate?: string) {
  document.getElementById('calendar-month')!.textContent = `${month.getFullYear()}年 ${month.getMonth()+1}月`;
  grid.replaceChildren();
  const monthPrefix=`${month.getFullYear()}-${String(month.getMonth()+1).padStart(2,'0')}-`;
  const monthly=record.dates.filter(date=>date.startsWith(monthPrefix)).length;
  calendarStatus.textContent=`この月は ${monthly}日読了 · 累計 ${record.lotus}日`;
  dialog.style.setProperty('--paper-wear',String(Math.min(record.lotus/100,1)));
  for(let i=0;i<month.getDay();i++){const blank=document.createElement('div');blank.className='calendar-blank';grid.append(blank);}
  const days=new Date(month.getFullYear(),month.getMonth()+1,0).getDate();
  for(let day=1;day<=days;day++) {
   const date=localDate(new Date(month.getFullYear(),month.getMonth(),day));
   const cell=document.createElement('div');cell.className='calendar-day';
   if(date===localDate(new Date()))cell.classList.add('today');
   const number=document.createElement('span');number.textContent=String(day);cell.append(number);
   const claimed=record.dates.includes(date);
   cell.setAttribute('aria-label',`${month.getMonth()+1}月${day}日${claimed?' 読経済み':''}`);
   if(claimed){const stamp=document.createElement('span');stamp.className='ritual-stamp';const ns='http://www.w3.org/2000/svg';
    const foot=document.createElementNS(ns,'svg');foot.setAttribute('viewBox','0 0 100 100');foot.setAttribute('focusable','false');
    const sole=document.createElementNS(ns,'path');
    sole.setAttribute('d','M 36 29 C 22 33 23 50 30 61 C 35 69 30 76 34 86 C 38 97 57 97 61 85 C 65 74 60 66 65 57 C 72 43 67 32 57 29 C 51 27 42 27 36 29 Z');
    sole.setAttribute('fill','currentColor');sole.setAttribute('fill-opacity','.18');foot.append(sole);
    for(const [cx,cy,rx,ry] of [[30,17,9,11],[49,14,6,8],[63,17,5,7],[74,24,4,6],[80,35,3.5,5]]){
     const toe=document.createElementNS(ns,'ellipse');for(const [name,value] of Object.entries({cx,cy,rx,ry}))toe.setAttribute(name,String(value));toe.setAttribute('fill','currentColor');toe.setAttribute('fill-opacity','.3');foot.append(toe);
    }
    // A small Dharma wheel in the sole marks this as a Buddha footprint.
    for(const radius of [3,12]){const circle=document.createElementNS(ns,'circle');circle.setAttribute('cx','47');circle.setAttribute('cy','48');circle.setAttribute('r',String(radius));circle.setAttribute('stroke-width','2');foot.append(circle);}
    for(let i=0;i<8;i++){const angle=i*Math.PI/4;const spoke=document.createElementNS(ns,'line');spoke.setAttribute('x1',String(47+Math.cos(angle)*3));spoke.setAttribute('y1',String(48+Math.sin(angle)*3));spoke.setAttribute('x2',String(47+Math.cos(angle)*12));spoke.setAttribute('y2',String(48+Math.sin(angle)*12));spoke.setAttribute('stroke-width','2');foot.append(spoke);}
    stamp.append(foot);stamp.setAttribute('aria-hidden','true');if(date===stampDate){stamp.classList.add('stamp-impact');cell.classList.add('ink-burst');}cell.append(stamp);}
   grid.append(cell);
  }
 }
 document.getElementById('daily-open')!.onclick=()=>{record=read();month=new Date();month.setDate(1);render();dialog.showModal();};
 document.getElementById('daily-close')!.onclick=()=>dialog.close();
 document.getElementById('month-prev')!.onclick=()=>{month.setMonth(month.getMonth()-1);drawCalendar();};
 document.getElementById('month-next')!.onclick=()=>{month.setMonth(month.getMonth()+1);drawCalendar();};
 function render() {
  const claimed = record.date === localDate(new Date());
  button.disabled = true;
  replay.hidden = !claimed;
  button.textContent = claimed ? '今日の印は押しました' : '最後まで読むと、今日の印がつきます';
  const today=new Date(), yesterday=new Date(today);yesterday.setDate(yesterday.getDate()-1);
  const streak=record.date===localDate(today)||record.date===localDate(yesterday)?record.streak:0;
  status.textContent = `連続 ${streak}日 · 仏足印 ${record.lotus}個`;
  calendarStatus.textContent=status.textContent;
  if(!animationTimer)drawCalendar();
 }
 function complete() {
  // Re-read before claiming so other tabs cannot normally claim twice.
  record = read();
  const now = new Date(), today = localDate(now);
  if(!dialog.open)dialog.showModal();
  if(record.date === today){render();openBookAndStamp(today);calendarStatus.textContent='読経を完了しました · 今日の読経済み';return;}
  const yesterday = new Date(now);yesterday.setDate(yesterday.getDate()-1);
  const next = {date:today,streak:record.date===localDate(yesterday)?record.streak+1:1,lotus:record.lotus+1,dates:[...record.dates,today]};
  try {localStorage.setItem(key,JSON.stringify(next));} catch {
   month=new Date(now);month.setDate(1);render();
   calendarStatus.textContent = '読経を完了しました。記録を保存できませんでした。';return;
  }
  record = next;
  render();openBookAndStamp(today);
  calendarStatus.textContent=`今日の仏足印がつきました。連続 ${record.streak}日 · 仏足印 ${record.lotus}個`;
 };
 function stopAnimation() {
  clearTimeout(animationTimer);animationTimer=undefined;
  bookScene.hidden=true;dialog.classList.remove('book-opening','calendar-impact');
  replay.disabled=false;
  for(const id of ['month-prev','month-next'])(document.getElementById(id) as HTMLButtonElement).disabled=false;
 }
 dialog.addEventListener('close',()=>{stopAnimation();render();});
 function openBookAndStamp(date:string) {
  stopAnimation();
  month=new Date();month.setDate(1);drawCalendar();
  replay.disabled=true;
  for(const id of ['month-prev','month-next'])(document.getElementById(id) as HTMLButtonElement).disabled=true;
  if(reducedMotion.matches){playStamp(date);return;}
  bookScene.hidden=false;void bookScene.offsetWidth;dialog.classList.add('book-opening');
  onPageTurn();
  animationTimer=setTimeout(()=>{
   bookScene.hidden=true;dialog.classList.remove('book-opening');
   if(dialog.open)playStamp(date);else stopAnimation();
  },1800);
 }
 function playStamp(date: string) {
  clearTimeout(animationTimer);
  month=new Date();month.setDate(1);
  drawCalendar(date);
  dialog.classList.remove('calendar-impact');void dialog.offsetWidth;dialog.classList.add('calendar-impact');
  animationTimer=setTimeout(stopAnimation,1700);
  const cell=grid.querySelector('.ink-burst');
  if(cell){
   for(let i=0;i<28;i++){
    const spark=document.createElement('i');spark.className='stamp-spark';spark.setAttribute('aria-hidden','true');
    const angle=i/28*Math.PI*2, distance=55+Math.random()*85;
    spark.style.setProperty('--spark-x',`${Math.cos(angle)*distance}px`);
    spark.style.setProperty('--spark-y',`${Math.sin(angle)*distance}px`);
    spark.style.setProperty('--spark-turn',`${Math.random()*360}deg`);
    spark.style.setProperty('--spark-color',i%2?'#eebb70':'#ef7356');cell.append(spark);
   }
  }
  onClaim();
 }
 replay.onclick=()=>{
  record=read();
  const today=localDate(new Date());
  render();
  if(record.date===today){openBookAndStamp(today);calendarStatus.textContent='今日の読経済み · 演出をもう一度';}
 };
 document.getElementById('daily-export')!.onclick=()=>{
  const link=document.createElement('a');
  link.href=URL.createObjectURL(new Blob([JSON.stringify(read())],{type:'application/json'}));
  link.download=`heart-sutra-${localDate(new Date())}.json`;
  link.click();
  setTimeout(()=>URL.revokeObjectURL(link.href),1000);
 };
 const importInput=document.getElementById('daily-import') as HTMLInputElement;
 document.getElementById('daily-import-button')!.onclick=()=>importInput.click();
 importInput.onchange=async()=>{
  const file=importInput.files?.[0];
  importInput.value='';
  if(!file)return;
  let imported: DailyRecord | undefined;
  try {imported=parseRecord(JSON.parse(await file.text()));} catch { /* Reported below. */ }
  if(!imported){calendarStatus.textContent='読み込めない記録ファイルです。';return;}
  const next=mergeRecords(read(),imported);
  try {localStorage.setItem(key,JSON.stringify(next));} catch {calendarStatus.textContent='記録を保存できませんでした。';return;}
  record=next;month=new Date();month.setDate(1);render();
  calendarStatus.textContent=`記録を統合しました。仏足印 ${record.lotus}個`;
 };
 window.addEventListener('storage',event=>{if(event.key===key){record=read();render();}});
 document.addEventListener('visibilitychange',()=>{if(!document.hidden){record=read();render();}});
 setInterval(render,30000);
 render();
 return {complete};
}
